import { useCallback } from 'react'

import { useParams } from 'react-router'
import { useExplanationForPatient } from 'src/features/explanation/api'
import { useTrial } from 'src/features/explanation/api/getTrial'
import { useSendVideoCallStartedNotification } from 'src/features/explanation/api/sendVideoCallStartedNotification'
import { getFullName } from 'src/utils/getFullName'

import { ExplanationRoomEventProvider } from '../../context/ExplanationRoomEvent'
import { usePatientLobby } from '../../hooks/usePatientLobby'
import { isAgreementCompletedOrRejected } from '../../utils/explanationRevisionStatusUtils'
import { ExplanationRoomLobbyVideoCallContent } from '../ExplanationRoomLobbyVideoCallContent/ExplanationRoomLobbyVideoCallContent'
import { PatientLobbyLayout } from '../PatientLobbyLayout/PatientLobbyLayout'

type Param = {
  trialUid: string
  explanationRevisionUid: string
  explanationReservationUid?: string // 必ず存在するはずだが、暫定的に旧仕様に対応するためoptionalにする
}

export const PatientLobbyScreen: React.FC = () => {
  const {
    trialUid = '',
    explanationRevisionUid = '',
    explanationReservationUid,
  } = useParams<Param>()

  const { preSession, moveToRoom } = usePatientLobby({
    trialUid,
    explanationRevisionUid,
    explanationReservationUid,
  })

  const { data: explanation } = useExplanationForPatient({
    explanationRevisionUid: preSession
      ? preSession.explanationRevision.uid
      : null,
  })

  const { data: trial } = useTrial({ trialUid })

  const { request: sendVideoCallStartedNotification } =
    useSendVideoCallStartedNotification()

  const sendNotification = useCallback(() => {
    if (!preSession || !explanation) return
    sendVideoCallStartedNotification({
      trialPatientUid: explanation.patient.trialPatientUid,
      scene: 'Explanation',
      sendTo: 'Patient',
    })
  }, [preSession, explanation, sendVideoCallStartedNotification])

  if (!preSession || !explanation || !trial) return null

  const sessionUid = preSession.explanationSessionUid

  return (
    <ExplanationRoomEventProvider
      roomType="PreSession"
      preSessionUid={preSession.uid}
      trialUid={trialUid}
      explanationRevisionUid={explanationRevisionUid}
      explanationPatientUid={explanation.patient.uid}
      patientPhoneNumber={explanation.patient.phoneNumber}
      deviceActorType="Patient"
      deviceActorUid={explanation.patient.uid}
      deviceActorTrialHospitalUid={explanation.trialHospital.uid}
    >
      <PatientLobbyLayout
        trialUid={trialUid}
        canMoveToRoom={!!sessionUid}
        featureChannel={trial.featureChannel}
        sidebarInfo={{
          trialName: trial.name,
          hospitalName: explanation.trialHospital.name,
          candidateId: explanation.patient.candidateId,
          members: preSession.explanationRevision.members.map(m => ({
            index: m.index,
            role: m.trialMember.role,
            name: getFullName(m.trialMember.user),
          })),
        }}
        isAgreementCompletedOrRejected={isAgreementCompletedOrRejected(
          preSession.explanationRevision.latestHistory.statusV2,
        )}
        onMoveToRoom={() => {
          if (!!sessionUid) {
            moveToRoom(sessionUid)
          }
        }}
      >
        <ExplanationRoomLobbyVideoCallContent
          canShareScreen={false}
          sendNotification={sendNotification}
        />
      </PatientLobbyLayout>
    </ExplanationRoomEventProvider>
  )
}
